import { Injectable } from "@angular/core";
import { roundToNearestMinutes } from "date-fns/esm";
import { Observable, timer } from "rxjs";
import { distinctUntilChanged, map, shareReplay } from "rxjs/operators";

const HALF_MINUTE = 30_000;
const FULL_MINUTE = 60_000;
const TICK_SKEW_MS = 100;

function minuteRoundedNow(): Date {
	return roundToNearestMinutes(new Date(), { nearestTo: 1 });
}

function msUntilNextHalfMinute(now = Date.now()): number {
	return HALF_MINUTE - (now % HALF_MINUTE) + TICK_SKEW_MS;
}

function msUntilNextFullMinute(now = Date.now()): number {
	return FULL_MINUTE - (now % FULL_MINUTE) + TICK_SKEW_MS;
}

@Injectable({
	providedIn: "root",
})
export class ClockService {
	private readonly intervals = new Map<number, Observable<Date>>();

	/**
	 * Minute-aligned wall time, shared app-wide. Emits immediately on subscribe,
	 * then whenever the half-up rounded minute changes (at the :30 boundary).
	 */
	readonly now$ = new Observable<Date>((subscriber) => {
		subscriber.next(minuteRoundedNow());
		const sub = timer(msUntilNextHalfMinute(), HALF_MINUTE)
			.pipe(map(() => minuteRoundedNow()))
			.subscribe(subscriber);
		return () => sub.unsubscribe();
	}).pipe(
		distinctUntilChanged((a, b) => a.getTime() === b.getTime()),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/**
	 * Wall-clock aligned to the full minute (:00). Emits immediately on subscribe,
	 * then at each subsequent :00. Use for refetch/polling that should not run at :30.
	 */
	readonly everyFullMinute$ = new Observable<Date>((subscriber) => {
		subscriber.next(new Date());
		const sub = timer(msUntilNextFullMinute(), FULL_MINUTE)
			.pipe(map(() => new Date()))
			.subscribe(subscriber);
		return () => sub.unsubscribe();
	}).pipe(shareReplay({ bufferSize: 1, refCount: true }));

	/** Precise wall time, ticks every second. */
	readonly nowPrecise$ = this.every$(1000);

	now(): Date {
		return minuteRoundedNow();
	}

	nowPrecise(): Date {
		return new Date();
	}

	/**
	 * Shared interval for arbitrary periods. Emits immediately on subscribe,
	 * then every `periodMs` — subscribers joining mid-cycle get the current time first.
	 */
	every$(periodMs: number): Observable<Date> {
		let cached = this.intervals.get(periodMs);
		if (!cached) {
			cached = new Observable<Date>((subscriber) => {
				subscriber.next(new Date());
				const sub = timer(periodMs, periodMs)
					.pipe(map(() => new Date()))
					.subscribe(subscriber);
				return () => sub.unsubscribe();
			}).pipe(shareReplay({ bufferSize: 1, refCount: true }));
			this.intervals.set(periodMs, cached);
		}
		return cached;
	}
}
